import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { reactivatePausedAccount, supabase } from '../../api/supabase';
import { useAppDispatch } from '../../store/hooks';
import { setAuthState } from '../../store/slices/authSlice';
import { IconButton, Box, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import balloonImg from '../../assets/balloon.png';
import SEOHead from '../../components/SEOHead';
import GooglePlayBanner from '../../components/GooglePlayBanner';
import { navigateAfterAuth } from '../../utils/authRedirect';
import IpCountryService from '../../services/ipCountryService';

// Heart Balloon Logo Component
const HeartBalloonLogo = ({ size = 40, color = '#A76FFF' }: { size?: number; color?: string }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="250 200 250 350" 
        fill={color}
        style={{ display: 'block' }}
    >
        <path d="M367.485,482.216 C365.074,470.485 365.074,470.414 353.833,470.350 C350.038,470.329 346.900,469.409 345.065,465.850 C343.139,462.115 344.228,458.731 346.546,455.607 C351.111,449.453 355.550,443.193 360.391,437.262 C363.052,434.003 362.530,432.349 359.352,429.736 C329.024,404.806 302.349,376.578 282.702,342.312 C271.940,323.541 264.986,303.322 265.906,281.482 C267.491,243.827 292.643,218.851 328.109,219.019 C344.845,219.099 359.566,225.480 371.952,236.913 C372.929,237.815 373.931,238.692 374.957,239.539 C375.189,239.731 375.564,239.750 376.155,239.939 C380.730,236.658 384.836,232.519 389.766,229.388 C428.264,204.939 479.549,224.948 485.407,274.396 C488.277,298.617 481.124,320.505 469.852,341.305 C457.780,363.583 441.577,382.796 424.065,400.970 C413.880,411.539 403.189,421.544 391.643,430.602 C389.221,432.503 389.048,433.927 390.890,436.302 C395.788,442.619 400.486,449.092 405.282,455.490 C407.605,458.589 409.027,461.830 406.948,465.625 C404.967,469.241 401.872,470.595 397.794,470.353 C394.308,470.147 390.794,470.435 387.302,470.276 C383.748,470.114 382.730,472.011 383.526,474.948 C384.568,478.793 385.737,482.654 387.359,486.281 C395.599,504.706 387.806,517.993 372.974,531.323 C369.668,534.294 365.050,533.489 362.116,530.437 C359.145,527.346 358.824,522.697 361.676,519.006 C364.113,515.852 366.958,513.018 369.509,509.948 C373.707,504.895 374.914,499.496 371.746,493.301 C370.015,489.917 368.958,486.188 367.485,482.216Z M467.707,270.788 C463.893,255.834 455.616,244.551 440.805,239.019 C420.887,231.579 399.311,238.600 384.945,256.912 C378.711,264.859 373.201,264.547 366.992,256.781 C354.663,241.363 338.678,233.315 318.784,236.950 C292.602,241.733 282.660,263.466 282.745,285.231 C282.799,299.339 286.924,312.822 293.223,325.469 C311.643,362.455 339.760,391.250 371.010,417.424 C374.872,420.659 377.488,420.281 381.218,417.251 C395.601,405.569 409.007,392.879 421.323,379.072 C436.546,362.006 450.752,344.172 459.930,322.945 C467.021,306.546 471.653,289.714 467.707,270.788Z M381.038,452.776 C380.366,449.709 378.035,447.941 375.962,445.680 C373.627,448.019 371.689,450.100 370.913,453.224 C374.432,453.900 377.526,453.708 381.038,452.776Z"/>
        <path d="M435.730,269.885 C444.007,272.849 448.592,282.420 447.142,293.171 C446.531,297.698 444.808,301.825 441.891,305.377 C438.712,309.247 434.199,310.119 430.387,307.691 C426.613,305.287 424.926,300.267 427.600,296.332 C431.008,291.315 430.639,287.215 427.553,282.203 C424.004,276.440 427.663,271.073 435.730,269.885Z"/>
    </svg>
);

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';

const LoginScreen = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [secureTextEntry, setSecureTextEntry] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Check if we came from a successful password reset
    useEffect(() => {
        if (location.state && (location.state as any).passwordResetSuccess) {
            setSuccessMessage('Your password has been reset successfully. You can now login with your new password.');
        }
    }, [location.state]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            if (data.user) {
                try {
                    await reactivatePausedAccount(data.user.id);
                } catch (accessError) {
                    await supabase.auth.signOut();
                    throw accessError;
                }
            }

            dispatch(setAuthState({
                user: data.user,
                session: data.session
            }));

            // Track user's IP country on successful login
            const ipCountryService = IpCountryService.getInstance();
            await ipCountryService.trackUserCountry(data.user?.id, 'user_login');

            // Track successful login with Meta Pixel
            if (typeof window !== 'undefined' && (window as any).fbq) {
                (window as any).fbq('track', 'Login', {
                    value: 0,
                    currency: 'USD',
                    content_name: 'User Login',
                    content_category: 'authentication'
                });
            }

            // Handle redirect after login
            navigateAfterAuth(navigate, location);
        } catch (error: any) {
            setError(error.message || 'An error occurred during login');
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = async (provider: 'google' | 'apple') => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: provider,
                options: {
                    redirectTo: `${window.location.origin}/auth/callback?signup=false`,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    }
                }
            });

            if (error) {
                if (error.message.includes('provider is not enabled')) {
                    setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login is not configured. Please contact support.`);
                } else {
                    throw error;
                }
            }
            // Note: The OAuth flow will redirect the user, so execution stops here
        } catch (error: any) {
            setError(error.message || `An error occurred during ${provider} login`);
            setLoading(false);
        }
    };

    // Basic styling - similar to RegisterEmailScreen
    const styles: { [key: string]: React.CSSProperties } = {
        mainContainer: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            background: 'linear-gradient(to bottom, #FFFFFF, #F8F0FF)',
        },
        scrollContainer: {
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
        },
        container: {
            width: '100%',
            maxWidth: '400px',
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px',
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: LIGHT_PURPLE,
            marginRight: '8px',
            letterSpacing: '1px',
        },
        subtitle: {
            fontSize: '18px',
            marginBottom: '30px',
            textAlign: 'center',
            color: '#666',
            fontWeight: 500,
        },
        formContainer: {
            margin: '0 10px',
        },
        inputWrapper: {
            marginBottom: '20px',
        },
        label: {
            display: 'block',
            marginBottom: '5px',
            color: '#333',
            fontSize: '14px',
            fontWeight: 500,
        },
        input: {
            width: '100%',
            padding: '12px 15px',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            fontSize: '16px',
            backgroundColor: '#F9F9F9',
            boxSizing: 'border-box',
        },
        passwordInputContainer: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
        },
        passwordInput: {
            width: '100%',
            padding: '12px 40px 12px 15px',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            fontSize: '16px',
            backgroundColor: '#F9F9F9',
            boxSizing: 'border-box',
        },
        passwordToggle: {
            position: 'absolute',
            right: '10px',
            cursor: 'pointer',
            color: LIGHT_PURPLE,
            userSelect: 'none',
        },
        loginButton: {
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: LIGHT_PURPLE,
            border: 'none',
            borderRadius: '30px',
            cursor: 'pointer',
            marginTop: '10px',
            marginBottom: '24px',
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        registerButton: {
            background: 'none',
            border: 'none',
            color: LIGHT_PURPLE,
            fontSize: '16px',
            cursor: 'pointer',
            textAlign: 'center',
            display: 'block',
            width: '100%',
            marginTop: '10px',
        },
        errorText: {
            color: VIBRANT_RED,
            fontSize: '14px',
            marginBottom: '15px',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 90, 118, 0.1)',
            display: error ? 'block' : 'none',
        },
        successText: {
            color: '#4CAF50',
            fontSize: '14px',
            marginBottom: '15px',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            display: successMessage ? 'block' : 'none',
        },
        forgotPasswordLink: {
            color: LIGHT_PURPLE,
            fontSize: '14px',
            textAlign: 'center',
            cursor: 'pointer',
            marginTop: '10px',
            display: 'block',
        },
        dividerContainer: {
            display: 'flex',
            alignItems: 'center',
            margin: '20px 0',
        },
        dividerLine: {
            flex: 1,
            height: '1px',
            backgroundColor: '#E0E0E0',
        },
        dividerText: {
            margin: '0 15px',
            color: '#666',
            fontSize: '14px',
        },
        socialButton: {
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            border: '1px solid #E0E0E0',
            borderRadius: '30px',
            cursor: 'pointer',
            marginBottom: '12px',
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            color: '#333',
            gap: '10px',
        },
        socialButtonsContainer: {
            marginBottom: '20px',
        }
    };

    return (
        <>
            <SEOHead
                title="Login - Pop The Balloon Dating App"
                description="Sign in to Pop The Balloon dating app to start finding your perfect match through our unique balloon-popping format."
                canonical="https://www.poptheballoonapp.com/login"
                ogTitle="Login to Pop The Balloon"
                ogDescription="Access your Pop The Balloon account and start connecting with singles near you."
                ogUrl="https://www.poptheballoonapp.com/login"
            />
            <div style={styles.mainContainer}>
            {/* Top Navigation */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                background: 'transparent'
            }}>
                <IconButton
                    onClick={() => navigate('/')}
                    sx={{
                        color: LIGHT_PURPLE,
                        '&:hover': {
                            backgroundColor: 'rgba(167, 111, 255, 0.1)'
                        }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Button
                    variant="text"
                    onClick={() => navigate('/register')}
                    sx={{
                        color: LIGHT_PURPLE,
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: 'rgba(167, 111, 255, 0.1)'
                        }
                    }}
                >
                    Sign Up
                </Button>
            </Box>

            <div style={styles.scrollContainer}>
                <div style={styles.container}>
                    <div style={styles.titleContainer}>
                        <span style={styles.title}>POP THE BALLOON</span>
                        <HeartBalloonLogo size={32} color={LIGHT_PURPLE} />
                    </div>

                    <p style={styles.subtitle}>Login to Your Account</p>

                    {error && <div style={styles.errorText}>{error}</div>}
                    {successMessage && <div style={styles.successText}>{successMessage}</div>}

                    <div style={styles.socialButtonsContainer}>
                        <button
                            type="button"
                            onClick={() => handleSocialLogin('google')}
                            style={styles.socialButton}
                            disabled={loading}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Continue with Google
                        </button>
                        
                        <button
                            type="button"
                            onClick={() => handleSocialLogin('apple')}
                            style={styles.socialButton}
                            disabled={loading}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                                <path fill="#000000" d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5M13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                            </svg>
                            Continue with Apple
                        </button>
                        
                    </div>

                    <div style={styles.dividerContainer}>
                        <div style={styles.dividerLine}></div>
                        <span style={styles.dividerText}>or</span>
                        <div style={styles.dividerLine}></div>
                    </div>

                    <form style={styles.formContainer} onSubmit={handleLogin}>
                        <div style={styles.inputWrapper}>
                            <label htmlFor="email" style={styles.label}>Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your.email@example.com"
                                style={styles.input}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div style={styles.inputWrapper}>
                            <label htmlFor="password" style={styles.label}>Password</label>
                            <div style={styles.passwordInputContainer}>
                                <input
                                    id="password"
                                    type={secureTextEntry ? 'password' : 'text'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Your password"
                                    style={styles.passwordInput}
                                    disabled={loading}
                                    required
                                />
                                <span
                                    style={styles.passwordToggle}
                                    onClick={() => setSecureTextEntry(!secureTextEntry)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setSecureTextEntry(!secureTextEntry); }}
                                >
                                    {secureTextEntry ? 'Show' : 'Hide'}
                                </span>
                            </div>
                        </div>

                        <button
                            type="submit"
                            style={styles.loginButton}
                            disabled={loading}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>

                        <span
                            style={styles.forgotPasswordLink}
                            onClick={() => navigate('/forgot-password')}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/forgot-password'); }}
                        >
                            Forgot password?
                        </span>

                        <button
                            type="button"
                            onClick={() => navigate('/register')}
                            style={styles.registerButton}
                        >
                            Don't have an account? Sign Up
                        </button>
                    </form>
                </div>
            </div>
            
            {/* Google Play Banner */}
            <GooglePlayBanner />
        </div>
        </>
    );
};

export default LoginScreen;
